// Audio validation utilities

/**
 * Check if file is a valid audio file
 */
export const isValidAudio = (file) => {
  const validTypes = [
    'audio/mp3',
    'audio/mpeg',
    'audio/wav',
    'audio/wave',
    'audio/x-wav',
    'audio/ogg',
    'audio/mp4',
    'audio/m4a',
    'audio/aac',
    'audio/x-aac',
    'audio/flac',
    'audio/x-flac',
    'audio/webm'
  ];
  
  const validExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.webm'];
  
  return validTypes.includes(file.type) || 
         validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
};

/**
 * Check if audio file size is valid
 */
export const isValidAudioSize = (file, maxSizeMB = 25) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Get audio file type from file
 */
export const getAudioType = (file) => {
  const typeMap = {
    'audio/mp3': 'MP3',
    'audio/mpeg': 'MP3',
    'audio/wav': 'WAV',
    'audio/wave': 'WAV',
    'audio/x-wav': 'WAV',
    'audio/ogg': 'OGG',
    'audio/mp4': 'M4A',
    'audio/m4a': 'M4A',
    'audio/aac': 'AAC',
    'audio/x-aac': 'AAC',
    'audio/flac': 'FLAC',
    'audio/x-flac': 'FLAC',
    'audio/webm': 'WEBM'
  };

  if (typeMap[file.type]) {
    return typeMap[file.type];
  }

  // Fallback to file extension
  const extension = file.name.split('.').pop()?.toUpperCase();
  return extension || 'AUDIO';
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get audio duration from file (returns Promise)
 */
export const getAudioDuration = (file) => {
  return new Promise((resolve) => {
    try {
      const audio = new Audio();
      const objectUrl = URL.createObjectURL(file);
      
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(audio.duration || 0);
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(0);
      };
      
      audio.src = objectUrl;
    } catch (error) {
      console.warn('Could not get audio duration:', error);
      resolve(0);
    }
  });
};

/**
 * Create audio preview URL
 */
export const createAudioPreview = (file) => {
  try {
    return URL.createObjectURL(file);
  } catch (error) {
    console.error('Error creating audio preview:', error);
    return null;
  }
};

/**
 * Revoke audio preview URL
 */
export const revokeAudioPreview = (url) => {
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Error revoking audio preview URL:', error);
    }
  }
};

/**
 * Format duration in MM:SS format
 */
export const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '--:--';
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get audio icon based on file type
 */
export const getAudioIcon = (type) => {
  // For now, return a generic audio icon
  // Could be extended to return different icons for different audio types
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" fill="#e74c3c"/>
      <path d="M19 10v2a7 7 0 01-14 0v-2" stroke="#e74c3c" strokeWidth="2" fill="none"/>
      <line x1="12" y1="19" x2="12" y2="23" stroke="#e74c3c" strokeWidth="2"/>
      <line x1="8" y1="23" x2="16" y2="23" stroke="#e74c3c" strokeWidth="2"/>
    </svg>
  );
};

/**
 * Validate multiple audio files
 */
export const validateAudioFiles = (files, maxFiles = 10, maxSizeMB = 25) => {
  const results = {
    validFiles: [],
    errors: []
  };

  if (files.length > maxFiles) {
    results.errors.push(`Maximum ${maxFiles} files allowed`);
    return results;
  }

  files.forEach((file, index) => {
    if (!isValidAudio(file)) {
      results.errors.push(`${file.name}: Unsupported audio format`);
      return;
    }

    if (!isValidAudioSize(file, maxSizeMB)) {
      results.errors.push(`${file.name}: File too large (max ${maxSizeMB}MB)`);
      return;
    }

    results.validFiles.push(file);
  });

  return results;
};
